import { getAuthHeaders } from "./client";
import { platformUrl } from "@/lib/api-config";

export type EmailProvider = "microsoft" | "imap";

export interface EmailConnectionStatus {
  enabled: boolean;
  microsoft: {
    enabled: boolean;
    connected: boolean;
    reconnectRequired: boolean;
    connectedAccountEmail: string | null;
  };
  imap: {
    enabled: boolean;
    connected: boolean;
    connectedAccountEmail: string | null;
  };
}

export interface EmailMessageSummary {
  providerMessageId: string;
  internetMessageId: string | null;
  conversationId: string;
  subject: string;
  from: string | null;
  to: string[];
  receivedAt: string;
  hasAttachments: boolean;
  attachmentCount: number;
  preview?: string;
  folderId?: string | null;
}

export interface EmailAttachment {
  id: string;
  messageId: string;
  fileName: string;
  mimeType: string;
  size: number;
  isInline?: boolean;
}

export interface EmailMessage extends EmailMessageSummary {
  bodyText: string;
  bodyHtml: string | null;
  attachments: EmailAttachment[];
}

export interface EmailConversation {
  conversationId: string;
  subject: string;
  participants: string[];
  messages: EmailMessage[];
}

export interface EmailFolder {
  id: string;
  displayName: string;
  totalItemCount?: number;
  unreadItemCount?: number;
}

export interface EmailPage<T> {
  items: T[];
  nextCursor: string | null;
}

export interface ImapConnectionInput {
  host: string;
  port: number;
  secure: boolean;
  username: string;
  password: string;
}

export interface EmailDownloadResponse {
  fileName: string;
  mimeType: string;
  data: string;
}

async function platformFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(platformUrl(path), {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(await getAuthHeaders()),
      ...options.headers,
    },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      typeof data === "object" && data && "error" in data
        ? String((data as { error: string }).error)
        : `Request failed (${response.status})`;
    throw new Error(message);
  }
  return data as T;
}

export function getEmailStatus(): Promise<EmailConnectionStatus> {
  return platformFetch("/api/integrations/email/status");
}

export function startMicrosoftEmailConnect(returnPath?: string): Promise<{ url: string }> {
  return platformFetch("/api/integrations/email/connect/microsoft", {
    method: "POST",
    body: JSON.stringify(returnPath ? { returnPath } : {}),
  });
}

export function connectImap(input: ImapConnectionInput): Promise<{ success: boolean }> {
  return platformFetch("/api/integrations/email/connect/imap", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function disconnectEmail(provider: EmailProvider): Promise<{ success: boolean }> {
  return platformFetch(`/api/integrations/email?provider=${provider}`, { method: "DELETE" });
}

export function listEmailFolders(provider: EmailProvider): Promise<{ folders: EmailFolder[] }> {
  return platformFetch(`/api/integrations/email/folders?provider=${provider}`);
}

export function searchEmailMessages(input: {
  provider: EmailProvider;
  query?: string;
  folderId?: string;
  hasAttachments?: boolean;
  limit?: number;
  cursor?: string;
}): Promise<EmailPage<EmailMessageSummary>> {
  const params = new URLSearchParams({ provider: input.provider });
  if (input.query) params.set("query", input.query);
  if (input.folderId) params.set("folderId", input.folderId);
  if (input.hasAttachments !== undefined) params.set("hasAttachments", String(input.hasAttachments));
  if (input.limit) params.set("limit", String(input.limit));
  if (input.cursor) params.set("cursor", input.cursor);
  return platformFetch(`/api/integrations/email/messages?${params.toString()}`);
}

export function getEmailConversation(
  provider: EmailProvider,
  conversationId: string,
  options?: { folderId?: string | null; messageId?: string | null },
): Promise<EmailConversation> {
  const params = new URLSearchParams({ provider });
  if (options?.folderId) params.set("folderId", options.folderId);
  if (options?.messageId) params.set("messageId", options.messageId);
  return platformFetch(
    `/api/integrations/email/conversations/${encodeURIComponent(conversationId)}?${params.toString()}`,
  );
}

export function getEmailMessage(
  provider: EmailProvider,
  messageId: string,
  folderId?: string | null,
): Promise<EmailMessage> {
  const params = new URLSearchParams({ provider });
  if (folderId) params.set("folderId", folderId);
  return platformFetch(`/api/integrations/email/messages/${encodeURIComponent(messageId)}?${params.toString()}`);
}

export function downloadEmailAttachment(input: {
  provider: EmailProvider;
  messageId: string;
  attachmentId: string;
  folderId?: string | null;
}): Promise<EmailDownloadResponse> {
  return platformFetch("/api/integrations/email/download", {
    method: "POST",
    body: JSON.stringify({ ...input, folderId: input.folderId || undefined }),
  });
}

export function emailDownloadToFile(response: EmailDownloadResponse): File {
  const binary = atob(response.data);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new File([bytes], response.fileName, { type: response.mimeType });
}

